// src/pages/Wishlist.jsx
import { useState, useEffect } from "react";
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next'; 
import { Heart, MapPin, Gauge, Calendar, Fuel, Trash2, ArrowRight } from 'lucide-react';
import { useWishlist } from '../../context/WishlistContext';
import { Seo } from "../../components/Seo";

const Skeleton = ({ className = "" }: { className?: string }) => (
  <div className={`animate-pulse bg-gray-200 rounded-lg ${className}`} />
);

function WishlistSkeleton({ t }: { t: any }) {
  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <Skeleton className="h-8 w-44 mb-2" />
            <Skeleton className="h-4 w-32" />
          </div>
          <Skeleton className="h-11 w-52 rounded-xl" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[...Array(6)].map((_, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden"
            >
              <div className="relative">
                <Skeleton className="h-48 w-full rounded-none" />
                <Skeleton className="absolute top-3 left-3 h-6 w-24 rounded-lg" />
                <Skeleton className="absolute top-3 right-3 w-9 h-9 rounded-lg" />
              </div>
              <div className="p-4">
                <Skeleton className="h-5 w-3/4 mb-2" />
                <Skeleton className="h-6 w-28 mb-4" />
                <div className="space-y-2 mb-5">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-5/6" />
                </div>
                <div className="flex gap-2">
                  <Skeleton className="flex-1 h-10 rounded-xl" />
                  <Skeleton className="w-28 h-10 rounded-xl" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function Wishlist() {
  const { t } = useTranslation('auth'); 
  const { wishlistItems, removeFromWishlist, refreshWishlist, wishlistCount, pagination } = useWishlist();
  const [comparing, setComparing] = useState<(string | number)[]>([]);
  const [loading, setLoading] = useState(() => !pagination);

  useEffect(() => {
    if (!pagination) {
      void refreshWishlist(1, 10).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const handlePageChange = async (newPage: number) => {
    if (!pagination || newPage === pagination.page_number || newPage < 1 || newPage > pagination.total_pages) return;
    setLoading(true);
    try {
      await refreshWishlist(newPage, 10);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Failed to change page', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleCompare = (id: string | number) => {
    setComparing(c => 
      c.includes(id) 
        ? c.filter(i => i !== id) 
        : c.length < 3 
          ? [...c, id] 
          : c
    );
  };

  if (loading) {
    return <WishlistSkeleton t={t} />;
  }

  if (wishlistCount === 0 || (!loading && wishlistItems.length === 0 && (!pagination || pagination.total_records === 0))) {
    return (
      <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-[#E5E7EB] max-w-md w-full">
          <div className="w-16 h-16 bg-[#F5F7FA] rounded-full flex items-center justify-center mx-auto mb-4">
            <Heart className="w-8 h-8 text-[#BFC2C7]" />
          </div>
          <h2 className="text-xl text-[#2D323A] mb-2 font-medium">
            {t('wishlist.title')}
          </h2>
          <p className="text-[#717182] mb-6 text-sm">
            {t('wishlist.description')}
          </p>
          <Link 
            to="/search" 
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors font-medium"
          >
            {t('wishlist.button')} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
    <Seo
  title={t('wishlist.seo.title')}
  description={t('wishlist.seo.description')}
/>
    <div className="min-h-screen bg-[#F5F7FA]">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl text-[#2D323A] font-semibold">
              {t('wishlist.title')}
            </h1>
            <p className="text-[#717182] text-sm">
              {wishlistCount} {wishlistCount === 1 ? t('wishlist.subtitle_single') : t('wishlist.subtitle')}
            </p>
          </div>
          {comparing.length >= 2 && (
            <Link 
              to={`/compare?ids=${comparing.join(',')}`} 
              className="px-5 py-2.5 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors shadow-md flex items-center gap-2 text-sm font-medium"
            >
              {t('wishlist.compare.button')} ({comparing.length}) <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {wishlistItems.map(v => (
            <div key={v.id} className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden hover:shadow-lg transition-shadow">
              <div className="relative">
                <img 
                  src={v.img || 'https://images.unsplash.com/photo-1617469767-1c6e04aaed8f?w=400&h=260&fit=crop'} 
                  alt={v.title} 
                  className="w-full h-48 object-cover" 
                />
                <button 
                  onClick={() => removeFromWishlist(v.id)} 
                  className="absolute top-3 right-3 p-2 bg-white/90 backdrop-blur rounded-lg shadow hover:bg-[#EF4444] hover:text-white transition-colors text-[#EF4444]"
                  title={t('wishlist.remove.title')}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                {v.savedAt && (
                  <div className="absolute top-3 left-3 px-2 py-1 bg-black/50 text-white text-xs rounded-lg">
                    {t('wishlist.saved')} {v.savedAt}
                  </div>
                )}
              </div>
              <div className="p-4">
                <h3 className="text-[#2D323A] font-semibold mb-1 truncate">{v.title}</h3>
                <p className="text-[#1E6DFF] font-semibold mb-3">
                  {typeof v.price === 'number' ? v.price.toLocaleString() : v.price} MAD
                </p>
                <div className="flex flex-wrap gap-2 text-xs text-[#717182] mb-4">
                  {v.year && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{v.year}</span>}
                  {v.km !== undefined && <span className="flex items-center gap-1"><Gauge className="w-3 h-3" />{typeof v.km === 'number' ? v.km.toLocaleString() : v.km} km</span>}
                  {v.fuel && <span className="flex items-center gap-1"><Fuel className="w-3 h-3" />{v.fuel}</span>}
                  {v.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{v.location}</span>}
                </div>
                <div className="flex gap-2">
                  <Link 
                    to={`/vehicle/${v.id}`} 
                    className="flex-1 py-2 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors text-center text-sm font-medium"
                  >
                    {t('wishlist.view_details')}
                  </Link>
                  <label className="flex items-center gap-1.5 px-3 py-2 border border-[#E5E7EB] rounded-xl hover:bg-[#F5F7FA] transition-colors cursor-pointer select-none">
                    <input 
                      type="checkbox" 
                      checked={comparing.includes(v.id)} 
                      onChange={() => toggleCompare(v.id)} 
                      className="accent-[#1E6DFF] w-3.5 h-3.5" 
                    />
                    <span className="text-xs text-[#717182]">{t('wishlist.compare.checkbox')}</span>
                  </label>
                </div>
              </div>
            </div>
          ))}
        </div>

        {pagination && pagination.total_pages > 1 && wishlistCount > 10 && (
          <div className="flex items-center justify-center gap-2 mt-10">
            <button
              onClick={() => handlePageChange(pagination.page_number - 1)}
              disabled={pagination.page_number <= 1}
              className="px-4 py-2 border border-[#E5E7EB] rounded-xl bg-white text-sm font-medium text-[#2D323A] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#F5F7FA] transition-colors cursor-pointer"
            >
              Previous
            </button>
            {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => handlePageChange(p)}
                className={`w-10 h-10 rounded-xl text-sm font-medium transition-colors cursor-pointer ${
                  p === pagination.page_number
                    ? 'bg-[#1E6DFF] text-white shadow-sm'
                    : 'bg-white border border-[#E5E7EB] text-[#2D323A] hover:bg-[#F5F7FA]'
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => handlePageChange(pagination.page_number + 1)}
              disabled={pagination.page_number >= pagination.total_pages}
              className="px-4 py-2 border border-[#E5E7EB] rounded-xl bg-white text-sm font-medium text-[#2D323A] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#F5F7FA] transition-colors cursor-pointer"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
    </>
  );
}